import React from 'react';
import { Clock } from 'lucide-react';
import { BusinessHours, DAY_LABELS, TIME_OPTIONS } from './businessHoursUtils';

interface BusinessHoursSectionProps {
  businessHours: BusinessHours;
  themePrimary: string;
  themeBorder: string;
  onToggleDay: (day: string) => void;
  onTimeChange: (day: string, timeType: 'startTime' | 'endTime', value: string) => void;
}

export function BusinessHoursSection({
  businessHours,
  themePrimary,
  themeBorder,
  onToggleDay,
  onTimeChange,
}: BusinessHoursSectionProps) {
  return (
    <div className="bg-card-background rounded-lg border border-border p-6">
      <div className="flex items-center mb-6">
        <Clock className="h-5 w-5 text-text-secondary mr-2" />
        <h2 className="text-lg font-medium text-text-primary">Business Hours</h2>
      </div>

      <div className="space-y-4">
        {Object.entries(DAY_LABELS).map(([dayKey, dayLabel]) => (
          <div key={dayKey} className="flex items-center py-2">
            <div className="w-24">
              <span className="text-sm font-medium text-text-primary">{dayLabel}</span>
            </div>

            <div className="flex items-center justify-center flex-1">
              {businessHours[dayKey].isOpen ? (
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <select
                      value={businessHours[dayKey].startTime}
                      onChange={(e) => onTimeChange(dayKey, 'startTime', e.target.value)}
                      className="input text-sm appearance-none"
                      style={{ '--tw-ring-color': themePrimary } as React.CSSProperties}
                    >
                      {TIME_OPTIONS.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <svg className="h-4 w-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  <span className="text-text-secondary text-sm">to</span>
                  <div className="relative">
                    <select
                      value={businessHours[dayKey].endTime}
                      onChange={(e) => onTimeChange(dayKey, 'endTime', e.target.value)}
                      className="input text-sm appearance-none"
                      style={{ '--tw-ring-color': themePrimary } as React.CSSProperties}
                    >
                      {TIME_OPTIONS.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <svg className="h-4 w-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => onToggleDay(dayKey)}
                className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none"
                style={{
                  backgroundColor: businessHours[dayKey].isOpen ? themePrimary : themeBorder
                }}
                role="switch"
                aria-checked={businessHours[dayKey].isOpen}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out absolute top-1 left-1 ${
                    businessHours[dayKey].isOpen ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-sm font-medium text-text-primary w-12">
                {businessHours[dayKey].isOpen ? 'Open' : 'Closed'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
